/**
 * NumberInput - Chakra UI Native Number Input Wrapper
 *
 * Full-featured number input component wrapping Chakra UI's NumberInput.Root.
 * Provides native Chakra UI theming, stepper controls, and formatting options.
 *
 * **Features**:
 * - Native Chakra UI stepper controls (increment/decrement)
 * - Number formatting with precision control
 * - Full Chakra UI theme integration
 * - Label, error, and hint support
 * - Accessible ARIA attributes
 *
 * **Use Cases**:
 * - Complex form fields with react-hook-form
 * - When you need Chakra UI theming consistency
 * - Forms requiring number formatting/parsing
 * - Accessible number inputs with stepper controls
 *
 * **When to Use InputNumber Instead**:
 * - For simple CRUD forms without complex formatting
 * - When you want lightweight custom +/- buttons
 * - If Chakra UI stepper styling doesn't fit your design
 *
 * @see InputNumber (input-number.tsx) for lightweight alternative
 * @module number-input
 */

import * as React from 'react';
import { NumberInput as ChakraNumberInput, Field, Box, Group } from '@chakra-ui/react';

export interface NumberInputProps {
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  containerClassName?: string;
  inputSize?: 'sm' | 'md' | 'lg';
  required?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  format?: (value: number) => string;
  parse?: (value: string) => number;
  id?: string;
  showStepper?: boolean;
}

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      label,
      error,
      hint,
      placeholder: _placeholder,
      containerClassName,
      inputSize = 'md',
      required,
      disabled,
      min,
      max,
      step = 1,
      precision,
      value,
      defaultValue,
      onChange,
      format: _format,
      parse: _parse,
      id,
      showStepper = true,
      ...props
    },
    ref
  ) => {
    // Generate ID for accessibility - always call React.useId() to comply with Rules of Hooks
    const generatedId = React.useId();
    const inputId = id || generatedId;

    // Determine field status
    const invalid = !!error;

    // Map sizes
    const sizeMap = {
      sm: 'sm',
      md: 'md',
      lg: 'lg',
    };

    return (
      <Field.Root
        {...(invalid ? { invalid: true } : {})}
        className={containerClassName}
        disabled={disabled}
      >
        {/* Label */}
        {label && (
          <Box
            as="label"
            fontSize="sm"
            fontWeight="medium"
            color={invalid ? 'fg.error' : 'fg.default'}
            display="block"
            marginBottom="4px"
          >
            {label}
            {required && (
              <Box as="span" color="fg.error" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}

        {/* Number Input */}
        <ChakraNumberInput.Root
          size={sizeMap[inputSize] as 'sm' | 'md' | 'lg'}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          value={value?.toString()}
          defaultValue={defaultValue?.toString()}
          onValueChange={(details: { valueAsNumber: number }) => onChange?.(details.valueAsNumber)}
          formatOptions={precision !== undefined ? { maximumFractionDigits: precision } : undefined}
          {...props}
        >
          <Group>
            <ChakraNumberInput.Input ref={ref} {...(inputId ? { 'aria-labelledby': inputId } : {})} />
            {showStepper && (
              <ChakraNumberInput.Control
                {...({} as Record<string, unknown> & { children?: React.ReactNode })}
              >
                <ChakraNumberInput.IncrementTrigger />
                <ChakraNumberInput.DecrementTrigger />
              </ChakraNumberInput.Control>
            )}
          </Group>
        </ChakraNumberInput.Root>

        {/* Error Message */}
        {error && (
          <Box fontSize="xs" color="fg.error" marginTop="4px">
            {error}
          </Box>
        )}

        {/* Hint Message */}
        {hint && !error && (
          <Box fontSize="xs" color="fg.muted" marginTop="4px">
            {hint}
          </Box>
        )}
      </Field.Root>
    );
  }
);

NumberInput.displayName = 'NumberInput';

// Export composable components for advanced usage
export const NumberInputRoot = ChakraNumberInput.Root;
export const NumberInputField = ChakraNumberInput.Input;
export const NumberInputControl = ChakraNumberInput.Control;
export const NumberInputIncrementTrigger = ChakraNumberInput.IncrementTrigger;
export const NumberInputDecrementTrigger = ChakraNumberInput.DecrementTrigger;

// Export as nested components for dot notation usage
export const NumberInputComponent = Object.assign(NumberInput, {
  Root: ChakraNumberInput.Root,
  Field: ChakraNumberInput.Input,
  Control: ChakraNumberInput.Control,
  IncrementTrigger: ChakraNumberInput.IncrementTrigger,
  DecrementTrigger: ChakraNumberInput.DecrementTrigger,
});

export { NumberInput };
